import { CapacitorUpdater, BundleInfo } from '@capgo/capacitor-updater'
import { SplashScreen } from '@capacitor/splash-screen'
import { Capacitor } from '@capacitor/core'

import { Logger } from '../logger'
import { OTAChannelConfig } from './types'

const logger = new Logger('OTAUpdater')

const OTA_CHANNEL_CONFIG_URL = 'https://cap.mred.com.br/channels'

export class OTAUpdater {
  async notifyAppReady() {
    await CapacitorUpdater.notifyAppReady()
    logger.success('App ready')
  }

  async checkUpdate() {
    logger.info('Starting OTA check...')

    const isNative = Capacitor.isNativePlatform()
    logger.info('Running on native device?', isNative)
    if (!isNative) {
      logger.info('Stopping check because running on Web platform.')
      return
    }

    await SplashScreen.show({ autoHide: false })

    const otaChannelConfig = await this.getChannelConfig()
    if (!otaChannelConfig) {
      logger.warn('No channel config found')
      await SplashScreen.hide()

      return
    }

    const currentBundle = await this.getCurrentBundle()
    if (!currentBundle) {
      logger.warn('No current bundle found')
      await SplashScreen.hide()

      return
    }

    const localSavedVersion = localStorage.getItem('OTA_LAST_VERSION') || ''

    const isUpToDate =
      (currentBundle.version || '').trim() === (otaChannelConfig.version || '').trim() ||
      localSavedVersion.trim() === (otaChannelConfig.version || '').trim()

    if (isUpToDate) {
      logger.info('Current bundle already up to date')
      await SplashScreen.hide()

      return
    }

    logger.info(`Update available: ${otaChannelConfig.version}`)

    const newBundle = await this.downloadUpdate(otaChannelConfig)
    if (!newBundle) {
      logger.error('Failed to download new bundle')
      await SplashScreen.hide()

      return
    }

    logger.success('Downloaded new bundle successfully')

    await this.setBundle(newBundle, otaChannelConfig)
    logger.success('Bundle set successfully')

    await SplashScreen.hide()
  }

  async getChannelConfig(): Promise<OTAChannelConfig | null> {
    logger.info('Getting channel config')
    const channel = process.env.REACT_APP_CHANNEL || 'production'

    logger.info('Using channel', channel)

    if (channel === 'local') {
      logger.info('OTA check skipped for local channel')
      return null
    }

    try {
      const response = await fetch(`${OTA_CHANNEL_CONFIG_URL}/${channel}.json`, { cache: 'no-store' })
      if (!response.ok) {
        logger.error(`Fetch channel config failed with status ${response.status}`)
        return null
      }

      const otaChannelConfig = (await response.json()) as OTAChannelConfig

      logger.info('Channel config', {
        version: otaChannelConfig.version,
        url: otaChannelConfig.url,
        hash: otaChannelConfig.hash,
      })

      if (!otaChannelConfig || !otaChannelConfig.version || !otaChannelConfig.url) {
        logger.warn('Invalid channel config')
        return null
      }

      return otaChannelConfig
    } catch (error) {
      logger.error('Error getting channel config', error)
      return null
    }
  }

  async getCurrentBundle(): Promise<BundleInfo | null> {
    try {
      const currentBundle = await CapacitorUpdater.current()
      logger.info('Current bundle', {
        id: currentBundle.bundle.id,
        version: currentBundle.bundle.version,
        downloaded: currentBundle.bundle.downloaded,
        checksum: currentBundle.bundle.checksum,
        status: currentBundle.bundle.status,
        native: currentBundle.native,
      })
      return currentBundle.bundle
    } catch (error) {
      logger.error('Error getting current bundle', error)
      return null
    }
  }

  async downloadUpdate(otaChannelConfig: OTAChannelConfig): Promise<BundleInfo | null> {
    try {
      const downloadedBundle = await CapacitorUpdater.download({
        url: otaChannelConfig.url,
        version: otaChannelConfig.version,
        checksum: otaChannelConfig.hash,
      })

      if (downloadedBundle.status === 'error') {
        logger.error('Download failed', { status: downloadedBundle.status })
        return null
      }

      logger.info('Downloaded bundle', {
        id: downloadedBundle.id,
        version: downloadedBundle.version,
        downloaded: downloadedBundle.downloaded,
        checksum: downloadedBundle.checksum,
        status: downloadedBundle.status,
      })
      return downloadedBundle
    } catch (error) {
      logger.error('Error downloading bundle', error)
      return null
    }
  }

  async setBundle(bundle: BundleInfo, otaChannelConfig: OTAChannelConfig) {
    try {
      logger.info(`Setting bundle ${bundle.id} (version ${otaChannelConfig.version})`)
      await CapacitorUpdater.set({ id: bundle.id })
      localStorage.setItem('OTA_LAST_VERSION', otaChannelConfig.version)
      logger.success('Bundle set successfully')
    } catch (error) {
      logger.error('Error setting bundle', error)
    }
  }
}
